import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { organizationApi } from "../services/organization-service";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const OrganizationMembers = ({ open, onClose, organization }) => {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addMemberDialog, setAddMemberDialog] = useState(false);
  const [editMemberDialog, setEditMemberDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    role: "viewer",
  });
  const [formLoading, setFormLoading] = useState(false);
  const [removeConfirmDialog, setRemoveConfirmDialog] = useState({
    open: false,
    member: null,
  });

  const getApiErrorMessage = (err, fallbackMessage) =>
    err.response?.data?.error?.message ||
    err.response?.data?.detail ||
    err.response?.data?.message ||
    err.message ||
    fallbackMessage;

  // Load members when dialog opens
  useEffect(() => {
    if (open && organization) {
      loadMembers();
    }
  }, [open, organization]);

  const loadMembers = async () => {
    if (!organization) return;

    setLoading(true);
    setError(null);
    try {
      const response = await organizationApi.getOrganizationMembers(
        organization.id,
      );
      setMembers(response.data);
    } catch (err) {
      console.error("Error loading members:", err);
      setError(t("organizationMembers.failedToLoad"));
      toast.error(t("organizationMembers.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!formData.email.trim()) {
      toast.error(t("organizationMembers.emailRequired"));
      return;
    }

    setFormLoading(true);
    try {
      await organizationApi.addOrganizationMember(organization.id, {
        email: formData.email,
        role_id: formData.role,
      });
      toast.success(t("organizationMembers.memberAdded"));
      setAddMemberDialog(false);
      setFormData({ email: "", role: "viewer" });
      loadMembers(); // Refresh the list
    } catch (err) {
      console.error("Error adding member:", err);
      const errorMessage = getApiErrorMessage(
        err,
        t("organizationMembers.failedToAddTeamMember"),
      );
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateMember = async () => {
    if (!selectedMember) return;

    setFormLoading(true);
    try {
      await organizationApi.updateOrganizationMember(
        organization.id,
        selectedMember.user_id,
        { role_id: formData.role },
      );
      toast.success(t("organizationMembers.memberUpdated"));
      setEditMemberDialog(false);
      setSelectedMember(null);
      setFormData({ email: "", role: "viewer" });
      loadMembers(); // Refresh the list
    } catch (err) {
      console.error("Error updating member:", err);
      const errorMessage = getApiErrorMessage(
        err,
        t("organizationMembers.failedToUpdateTeamMember"),
      );
      toast.error(errorMessage);
    } finally {
      setFormLoading(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!member) return;

    setRemoveConfirmDialog({ open: true, member });
  };

  const confirmRemoveMember = async () => {
    const { member } = removeConfirmDialog;
    if (!member) return;

    try {
      await organizationApi.removeOrganizationMember(
        organization.id,
        member.user_id,
      );
      toast.success(t("organizationMembers.memberRemoved"));
      loadMembers(); // Refresh the list
    } catch (err) {
      console.error("Error removing member:", err);
      const errorMessage = getApiErrorMessage(
        err,
        t("organizationMembers.failedToRemoveTeamMember"),
      );
      toast.error(errorMessage);
    } finally {
      setRemoveConfirmDialog({ open: false, member: null });
    }
  };

  const handleEditMember = (member) => {
    setSelectedMember(member);
    setFormData({
      email: member.user?.email || "",
      role: getRoleName(member.role_id),
    });
    setEditMemberDialog(true);
  };

  const getRoleColor = (roleId) => {
    switch (roleId) {
      case "owner":
        return "error";
      case "editor":
        return "warning";
      case "viewer":
        return "primary";
      default:
        return "default";
    }
  };

  const getRoleIcon = (roleId) => {
    switch (roleId) {
      case "owner":
        return <AdminIcon fontSize="small" />;
      case "editor":
        return <EditIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  const getRoleName = (roleId) => {
    switch (roleId) {
      case "owner":
        return t("organizationMembers.owner");
      case "editor":
        return t("aiCatalog.editor");
      case "viewer":
        return t("aiCatalog.viewer");
      default:
        return t("organizationMembers.unknown");
    }
  };

  const canManageMember = (member) => {
    // Users can manage members except themselves
    // This would typically check against the current user's ID
    // For now, allow management of all members
    return true;
  };

  const canRemoveMember = (member) => {
    // Prevent removing the last owner
    const owners = members.filter((m) => m.role_id === "owner");
    return !(member.role_id === "owner" && owners.length === 1);
  };

  if (!organization) return null;

  return (
    <>
      {/* Main Members Dialog */}
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <PersonAddIcon />
            <Typography variant="h6">
              {t("organizationMembers.manageMembersTitle", {
                name: organization.name,
              })}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">
              {t("organizationMembers.membersCount", {
                count: members.length,
              })}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddMemberDialog(true)}
            >
              {t("organizationMembers.addMember")}
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : members.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <PersonIcon
                sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t("organizationMembers.noMembers")}
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {t("organizationMembers.noMembersDescription")}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddMemberDialog(true)}
              >
                {t("organizationMembers.addFirstMember")}
              </Button>
            </Box>
          ) : (
            <List>
              {members.map((member, index) => (
                <React.Fragment key={member.user_id}>
                  <ListItem>
                    <Avatar sx={{ marginInlineEnd: 2 }}>
                      {member.avatar_url ? (
                        <img src={member.avatar_url} alt={member.name} />
                      ) : (
                        <PersonIcon />
                      )}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography variant="subtitle1">
                            {member.user?.first_name && member.user?.last_name
                              ? `${member.user.first_name} ${member.user.last_name}`
                              : member.user?.username ||
                                member.user?.email ||
                                t("organizationMembers.unknown")}
                          </Typography>
                          <Chip
                            label={getRoleName(member.role_id)}
                            color={getRoleColor(member.role_id)}
                            size="small"
                            icon={getRoleIcon(member.role_id)}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {member.user?.email || t("common.notAvailable")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t("common.created")}:{" "}
                            {new Date(member.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {canManageMember(member) && (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title={t("organizationMembers.editMember")}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditMember(member)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip
                            title={
                              canRemoveMember(member)
                                ? t("organizationMembers.removeMember")
                                : t("common.notAvailable")
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={!canRemoveMember(member)}
                                onClick={() => handleRemoveMember(member)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < members.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} sx={{ color: "text.primary" }}>
            {t("organizationMembers.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog
        open={addMemberDialog}
        onClose={() => setAddMemberDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("organizationMembers.addMemberTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("common.emailAddress")}
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>{t("common.role")}</InputLabel>
            <Select
              value={formData.role}
              label={t("common.role")}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
            >
              <MenuItem value="viewer">{t("aiCatalog.viewer")}</MenuItem>
              <MenuItem value="editor">{t("aiCatalog.editor")}</MenuItem>
              <MenuItem value="owner">{t("organizationMembers.owner")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setAddMemberDialog(false)}
            disabled={formLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            color="primary"
            disabled={formLoading}
          >
            {formLoading
              ? <CircularProgress size={20} />
              : t("organizationMembers.addMember")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog
        open={editMemberDialog}
        onClose={() => setEditMemberDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("organizationMembers.editMemberTitle")}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t("common.emailAddress")}
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            disabled
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth>
            <InputLabel>{t("common.role")}</InputLabel>
            <Select
              value={formData.role}
              label={t("common.role")}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
            >
              <MenuItem value="viewer">{t("aiCatalog.viewer")}</MenuItem>
              <MenuItem value="editor">{t("aiCatalog.editor")}</MenuItem>
              <MenuItem value="owner">{t("organizationMembers.owner")}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setEditMemberDialog(false)}
            disabled={formLoading}
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleUpdateMember}
            variant="contained"
            color="primary"
            disabled={formLoading}
          >
            {formLoading ? <CircularProgress size={20} /> : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <Dialog
        open={removeConfirmDialog.open}
        onClose={() => setRemoveConfirmDialog({ open: false, member: null })}
      >
        <DialogTitle>{t("organizationMembers.removeMember")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("organizationMembers.removeMemberConfirm", {
              email:
                removeConfirmDialog.member?.user?.first_name &&
                removeConfirmDialog.member?.user?.last_name
                  ? `${removeConfirmDialog.member.user.first_name} ${removeConfirmDialog.member.user.last_name}`
                  : removeConfirmDialog.member?.user?.email ||
                    t("organizationMembers.unknown"),
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() =>
              setRemoveConfirmDialog({ open: false, member: null })
            }
            color="secondary"
            variant="text"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={confirmRemoveMember}
            color="error"
            variant="contained"
          >
            {t("organizationMembers.removeMember")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OrganizationMembers;
